import React, { useState, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { PDFDocument } from 'pdf-lib';
import './PdfSignerSelector.css';

// Configuración del worker
// pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const PdfSignerSelector = ({ 
    pdfFile, 
    signFieldAlias = "firma_qr",    
    boxConfig = {
        backgroundColor: 'rgba(0, 123, 255, 0.2)',
        borderColor: '#007bff',
        username:"",
        text: 'QR',
        stamp_width: 150,
        stamp_height: 50
    },
    onConfirmLocation,
    onSelectionComplete // Callback que devuelve el objeto listo para la API
}) => {
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
    const [containerWidth, setContainerWidth] = useState(0);
    const [visualCoords, setVisualCoords] = useState({ x: 0, y: 0 });
    const [nextFieldName, setNextFieldName] = useState(signFieldAlias);
    
    const containerRef = useRef(null);
    const scaleFactor = containerWidth < 800 ? containerWidth / 800 : 1;
    // Dimensiones constantes de la estampa
    // const STAMP_W = 150; 
    // const STAMP_H = 50;

    useEffect(() => {
        const handleKeyDown = (e) => {
            const tag = document.activeElement?.tagName?.toLowerCase();
            if (tag === 'input' || tag === 'textarea') {
                return;
            }
            // Flecha izquierda
            if (e.key === 'ArrowLeft') {
                setPageNumber(prev => Math.max(prev - 1, 1));
            }
            // Flecha derecha
            if (e.key === 'ArrowRight') {
                setPageNumber(prev => Math.min(prev + 1, numPages));
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [numPages]);

    // 1. Detectar siguiente alias de campo (firma_qr, firma_qr1...)
    useEffect(() => {
        if (pdfFile) {
            const scanFields = async () => {
                const arrayBuffer = await pdfFile.arrayBuffer();
                const pdfDoc = await PDFDocument.load(arrayBuffer);
                const fieldNames = pdfDoc.getForm().getFields().map(f => f.getName());
                
                let name = signFieldAlias;
                let counter = 1;
                while (fieldNames.includes(name)) {
                    name = `${signFieldAlias}${counter}`;
                    counter++;
                }
                setNextFieldName(name);
            };
            scanFields();
        }
    }, [pdfFile, signFieldAlias]);

    // 2. Manejo de redimensionamiento
    useEffect(() => {
        const handleResize = () => {
            if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth);
        };
        console.log(containerRef.current.offsetWidth);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const onPageLoadSuccess = (page) => {
        const [ , , width, height] = page.view;
        setPageSize({ width, height });
    };

    const handlePdfClick = (e) => {
        const canvas = e.currentTarget.querySelector('canvas');
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const xPx = e.clientX - rect.left;
        const yPx = e.clientY - rect.top;
        // console.log(xPx);
        // console.log(yPx);
        const scaleX = pageSize.width / rect.width;
        const scaleY = pageSize.height / rect.height;

        // console.log(boxConfig);
        // Coordenadas iText7 (Esquina inferior izquierda)
        const itextX = Math.round((xPx * scaleX) - (boxConfig.stamp_width / 2));
        const itextY = Math.round((pageSize.height - (yPx * scaleY)) - (boxConfig.stamp_height / 2));
        
        setVisualCoords({ x: (xPx / rect.width) * 100, y: (yPx / rect.height) * 100 });

        // Enviar datos al padre
        if (onSelectionComplete) {
            
            onSelectionComplete({
                posicionX: itextX,
                posicionY: itextY,
                pagina: pageNumber,
                signField: nextFieldName
            });
        }
    };
    const today = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        // hour: '2-digit',
        // minute: '2-digit',
        // second: '2-digit'
    }).format(new Date());
    return (
        <div className="pdf-selector-module" ref={containerRef}>
            <div className="pdf-nav-controls">
                <button onClick={() => setPageNumber(prev => Math.max(prev - 1, 1))} disabled={pageNumber <= 1}>◀</button>
                <div className="page-input-container">
                    <span>Página</span>
                    <input
                        type="number"
                        min="1"
                        max={numPages || 1}
                        value={pageNumber}
                        onChange={(e) => {
                            const value = Number(e.target.value);
                            if (!value) {
                                setPageNumber(1);
                                return;
                            }
                            if (value >= 1 && value <= numPages) {
                                setPageNumber(value);
                            }
                        }}
                        className="page-number-input"
                    />

                    <span>de {numPages}</span>
                </div>
                <button onClick={() => setPageNumber(prev => Math.min(prev + 1, numPages))} disabled={pageNumber >= numPages}>▶</button>
            </div>

            <div className="pdf-canvas-wrapper" onClick={handlePdfClick}>
                <Document file={pdfFile} onLoadSuccess={({numPages}) => setNumPages(numPages)}>
                    <Page 
                        pageNumber={pageNumber} 
                        width={containerWidth - 20} 
                        onLoadSuccess={onPageLoadSuccess}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                    />
                </Document>

                {visualCoords.x !== 0 && (
                    <div className="signature-box-wrapper" style={{
                        left: `${visualCoords.x}%`,
                        top: `${visualCoords.y}%`,
                        position: 'absolute',                        
                        zIndex: 100
                    }}>
                        {/* El diseño de la estampa que definimos antes */}
                        <div className="signature-box-container" style={{
                            transform: `translate(-50%, -50%) scale(${scaleFactor})`,
                            backgroundColor: boxConfig.backgroundColor || 'rgba(255, 255, 255, 0.9)',
                            border: `1px solid ${boxConfig.borderColor || '#003366'}`
                        }}>
                            <div className="sig-qr-mock"></div>
                            <div className="sig-text-info">
                                <span className="sig-label">FIRMADO ELECTRONICAMENTE POR:</span>
                                <span className="sig-name">{boxConfig.userName || 'NOMBRE APELLIDO'}</span>
                                <span className="sig-date">{today}</span>
                            </div>
                        </div>

                        {/* --- EL NUEVO BOTÓN CONTINUAR --- */}
                        <button className='btn-continuar'
                            onClick={(e) => {
                                e.stopPropagation(); // Evita que el clic dispare un nuevo movimiento de estampa
                                if (onConfirmLocation) onConfirmLocation(); // Nueva prop para avisar al padre
                            }}
                        >
                            Continuar
                        </button>
                    </div>
                    // <div className="signature-box-container" style={{
                    //     left: `${visualCoords.x}%`,
                    //     top: `${visualCoords.y}%`,
                    //     backgroundColor: boxConfig.backgroundColor || 'rgba(255, 255, 255, 0.9)',
                    //     border: `1px solid ${boxConfig.borderColor || '#003366'}`
                    // }}>
                    //     {/* Columna Izquierda: QR */}
                    //     <div className="sig-qr-mock">
                    //         <div className="qr-pixel-grid"></div> {/* Simulación visual de QR */}
                    //     </div>

                    //     {/* Columna Derecha: Texto informativo */}
                    //     <div className="sig-text-info">
                    //         <span className="sig-label">FIRMADO ELECTRONICAMENTE POR:</span>
                    //         <span className="sig-name">{boxConfig.userName || 'NOMBRE APELLIDO'}</span>
                    //         <span className="sig-date">{today}</span>
                    //     </div>
                        
                    //     {/* Botón para limpiar selección (opcional, solicitado antes) */}
                    //     <button 
                    //         className="sig-clear-btn"
                    //         onClick={(e) => {
                    //             e.stopPropagation();
                    //             setVisualCoords({ x: 0, y: 0 });
                    //             onSelectionComplete(null);
                    //         }}
                    //     >
                    //         ✕
                    //     </button>
                    //     <button 
                    //         onClick={(e) => {
                    //             e.stopPropagation(); // Evita que el clic dispare un nuevo movimiento de estampa
                    //             if (onConfirmLocation) onConfirmLocation(); // Nueva prop para avisar al padre
                    //         }}
                    //         style={{
                    //             marginTop: '10px',
                    //             width: '100%',
                    //             backgroundColor: '#28a745',
                    //             color: 'white',
                    //             border: 'none',
                    //             borderRadius: '4px',
                    //             padding: '8px',
                    //             fontWeight: 'bold',
                    //             cursor: 'pointer',
                    //             boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    //         }}
                    //     >
                    //         Continuar con esta ubicación
                    //     </button>
                    // </div>
                )}
            </div>
        </div>
    );
};

export default PdfSignerSelector;